import { Component, EventEmitter, Input, Output } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { FirmwareService } from '../services/firmware/firmware.service';

@Component({
  selector: 'dcc-create-module-version',
  templateUrl: './create-module-version.component.html',
  styleUrls: ['./create-module-version.component.scss'],
})
export class CreateModuleVersionComponent {
  @Input() moduleName: string;
  @Output() closePage: EventEmitter<any> = new EventEmitter();
  @Output() refreshPage: EventEmitter<any> = new EventEmitter();

  name = '';
  version = '';
  platform = '';
  signature = '';
  showFileInfo = false;
  moduleFile: File | undefined = undefined;
  signatureFile: File | undefined = undefined;
  configJson: JSON | undefined = undefined;
  zsyncFile: File | undefined = undefined;
  zsyncFileName = '';
  isLoading = false;
  canEnableAddButton = false;
  isModuleNameMisMatch = false;

  constructor(
    private toastr: ToastrService,
    private firmwareService: FirmwareService,
    private activeModal: NgbActiveModal,
  ) {}

  async createModuleVersion() {
    if (!this.isModuleVersionValid()) {
      return;
    }
    try {
      this.isLoading = true;
      this.firmwareService
        .postModuleVersion(
          this.moduleName,
          this.moduleFile,
          this.signature.trim(),
          this.configJson,
          this.zsyncFile,
        )
        .subscribe({
          next: (response) => {
            this.isLoading = false;
            this.toastr.success('Created Module Version: ' + this.moduleName);
            this.refreshPage.emit();
            this.close();
          },
          error: (error) => {
            this.isLoading = false;
            this.toastr.error('Upload error:', error);
          },
        });
    } catch (e) {
      this.toastr.error(`Failed to submit the new request, please try again.`);
      return;
    }
  }

  isModuleVersionValid(): boolean {
    if (!this.verifyMatchingFiles()) {
      return false;
    }
    if (!this.checkValidModuleName()) {
      return false;
    }
    if (!this.signature.length) {
      this.toastr.warning(
        `Module package signature is required. This is provided in the *.sig file`,
      );
      return false;
    }
    if (!this.moduleFile || this.moduleFile.size == 0) {
      this.toastr.warning(
        `A Module file with an optional JSON configuration file is required`,
      );
      return false;
    }
    return true;
  }

  onSelectModule(e: Event) {
    const file = e.target as HTMLInputElement;
    if (file.files) {
      const moduleFile = file.files[0];
      const split = moduleFile.name.split('_');
      if (split.length != 3) {
        this.toastr.error(`Invalid file name`);
        return;
      }

      this.moduleFile = moduleFile;
      this.name = split[0];
      this.version = split[1];
      this.platform = split[2] === 'aarch64.ipk' ? 'Otto' : 'LcFlex';
      this.showFileInfo = true;

      if (this.checkValidModuleName()) this.verifyMatchingFiles();
    }
  }

  verifyMatchingFiles(): boolean {
    if (!this.moduleFile || !this.signatureFile) {
      return true;
    }

    this.canEnableAddButton = this.checkValidModuleName();
    const moduleInfo = this.getFileInfo(this.moduleFile);
    const sigInfo = this.getFileInfo(this.signatureFile);

    const matching =
      moduleInfo.name === sigInfo.name &&
      moduleInfo.platform === sigInfo.platform &&
      moduleInfo.version === sigInfo.version;

    if (!matching) {
      this.canEnableAddButton = false;
      this.toastr.error('Module and signature files do not match.');
    }

    return matching;
  }

  getFileInfo(file: File) {
    const split = file.name.split('_');
    return {
      name: split[0],
      version: split[1],
      platform: split[2].substring(0, split[2].indexOf('.')),
    };
  }

  async onSelectSignature(e: Event) {
    const file = e.target as HTMLInputElement;
    if (file.files) {
      this.signatureFile = file.files[0];
      this.signature = await this.blobToBase64(this.signatureFile);
      this.verifyMatchingFiles();
    }
  }

  blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, _) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.readAsText(blob);
    });
  }

  onSelectZsync(e: Event) {
    const file = e.target as HTMLInputElement;
    if (file.files && file.files.length) {
      this.zsyncFile = file.files[0];
      this.zsyncFileName = this.zsyncFile.name;
    }
  }

  async onSelectJsonUpload(e: Event) {
    const file = e.target as HTMLInputElement;
    if (file.files) {
      const jsonFile = file.files[0];
      this.configJson = JSON.parse(await this.blobToBase64(jsonFile));
      this.showFileInfo = true;
    }
  }

  checkValidModuleName() {
    if (this.moduleName && this.name && this.moduleName !== this.name) {
      this.toastr.error(
        `The filename: '${this.name}' must match the Module Name :'${this.moduleName}'`,
      );
      this.isModuleNameMisMatch = true;
      this.canEnableAddButton = false;
      return false;
    }
    this.isModuleNameMisMatch = false;
    return true;
  }

  close() {
    this.activeModal.close(false);
  }
}
